import * as React from 'react'
import { useState, useEffect, useRef } from 'react'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Paper from '@material-ui/core/Paper'
import Typography from '@material-ui/core/Typography'
import CircularProgress from '@material-ui/core/CircularProgress'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import TextField from '@material-ui/core/TextField'
import IconButton from '@material-ui/core/IconButton'
import SendIcon from '@material-ui/icons/Send'
import Avatar from '@material-ui/core/Avatar'
import { useSelector } from 'react-redux'
import { selectMyProfileData } from '../../../dashboard/widgets/MyProfile/myProfileSlice'
import GlobalAiChatStyles from './globalAiChatStyles'
import CheckIsMobile from '../../../../../utils/isMobileHook'

// redirect login to chat API (required for now)
// first use :REACT_APP_ITSTAFF_EMBEDCHAT_REDIRECT="https://rutgerssandbox.service-now.com/sn_va_web_client_login.do?sysparm_redirect_uri="
// get url param: ?itServiceCenter=true
// Then use:  process.env.REACT_APP_ITSTAFF_EMBEDCHAT
// Department data
const departments = [
  {
    title: 'Service Now',
    description:
      'requesting services, find IT related answers, check ticket and service status, acces to help desk and service center, meail, technical question, access',
    iFrameLink: process.env.REACT_APP_ITSTAFF_EMBEDCHAT,
    info: '',
  },
  {
    title: 'Student Services Center',
    description:
      'requesting information, graduation, financial aid, taking classes, scholarship, orientation, map, campus information',
    iFrameLink:
      'https://ai-backend2--rutgers-university-213217.us-central1.hosted.app/chatAssistant',
    info: '',
  },
]

function GlobalChatCore() {
  const styles = GlobalAiChatStyles()
  const [isOpen, setIsOpen] = useState(false)
  const [isTyping, setIsTyping] = useState(false)
  const [messages, setMessages] = useState([])
  const [inputMessage, setInputMessage] = useState('')
  const [showIframe, setShowIframe] = useState(false)
  const [iframeLink, setIframeLink] = useState('')
  const [iframeTitle, setIframeTtile] = useState('')
  const profileData = useSelector(selectMyProfileData)
  const isMobile = CheckIsMobile()
  console.log('[GlobalChatCore]: isMobile: ', isMobile)

  const [enableDialogBoolean, setEnableChatDialogBoolean] = useState(false)
  const chatBodyRef = useRef(null)

  // This useEffect hook handles the auto-scrolling of the chat body.
  useEffect(() => {
    if (chatBodyRef.current) {
      chatBodyRef.current.scrollTop = chatBodyRef.current.scrollHeight
    }
  }, [messages])

  // Currenlty required to open up chat with service now iFrame when redirected
  React.useEffect(() => {
    const windowLoc = window.location.href
    if (windowLoc.includes('?itServiceCenter=true')) {
      setIsOpen(true)
      setIframeTtile('Service Now')
      setIframeLink(process.env.REACT_APP_ITSTAFF_EMBEDCHAT)
      setShowIframe(true)
      console.log('[GlobalChatCore]: service link switch')
    }
  }, [])
  // check lint error below...
  // Logic should reflect and access the state of iFrame content,
  // If no user data, do not render chatbubble
  React.useEffect(() => {
    if (profileData?.length === 0) {
      setEnableChatDialogBoolean(false)
      console.log('[GlobalChatCore]: user not logged in')
    } else {
      setEnableChatDialogBoolean(true)
      console.log(
        '[GlobalChatCore]: user logged in, chat enabled',
        enableDialogBoolean,
      )
    }
  }, [profileData])

  React.useEffect(() => {
    // if the selected depratment is not service now do not execute redirec logic
    // this should be updated as additonal chat bot links added (should redirect logic is needed)
    if (iframeTitle !== 'Service Now') return
    const redirectURL = `${process.env.REACT_APP_IMAGES_URL}?itServiceCenter=true`

    // if iframe title is set to Service Now (by user when selecting department button in chat window)

    const handleMessage = (e) => {
      console.log('event info ', e.data)
      if (e.data.type === 'SESSION_CREATED' && !e.data.authenticated) {
        // redirect to SSO login if the chat widget logs in but user isnt authenticated
        window.location.href =
          process.env.REACT_APP_ITSTAFF_EMBEDCHAT_REDIRECT +
          encodeURIComponent(redirectURL)
      }

      // redirect to SSO login if the ServiceNow platform logs out from underneath the chat widget
      if (e.data.type === 'SESSION_LOGGED_OUT') {
        window.location.href =
          process.env.REACT_APP_ITSTAFF_EMBEDCHAT_REDIRECT +
          encodeURIComponent(redirectURL)
      }
    }
    window.addEventListener('message', handleMessage)
    // eslint-disable-next-line
    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [iframeTitle])

  const handleToggleChat = () => {
    setIsOpen(!isOpen)
  }

  const handleSendMessage = () => {
    if (inputMessage.trim() !== '') {
      const userInputWords = inputMessage.toLowerCase().split(/\s+/)
      setMessages((prevMessages) => [
        ...prevMessages,
        { text: inputMessage, sender: 'user', id: Date.now() },
      ])
      setInputMessage('')

      const foundDepartment = departments.find((dept) => {
        const titleAndDescWords = `${dept.title} ${dept.description}`
          .toLowerCase()
          .split(/\s+/)
        return titleAndDescWords.some((word) => userInputWords.includes(word))
      })

      if (foundDepartment) {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            type: 'button',
            text: foundDepartment.title,
            link: foundDepartment.iFrameLink,
            sender: 'bot',
            id: Date.now() + 1,
          },
        ])
      } else {
        setMessages((prevMessages) => [
          ...prevMessages,
          {
            text: 'I cannot find an exact-match for your request, but please choose from the available department chats for more information:',
            sender: 'bot',
            id: Date.now() + 1,
          },
          ...departments.map((dept, index) => ({
            type: 'button',
            text: dept.title,
            link: dept.iFrameLink,
            sender: 'bot',
            id: Date.now() + 2 + index,
          })),
        ])
      }
    }
  }

  const handleDepartmentButtonClick = (link) => {
    const departmentTitle = departments.find(
      (dept) => link === dept.iFrameLink && dept,
    )
    setIframeLink(link)
    setShowIframe(true)
    setIframeTtile(departmentTitle.title) // set chat window header info (title for current chat)
  }

  const handleInputChange = (event) => {
    setInputMessage(event.target.value)
  }

  useEffect(() => {
    if (isOpen) {
      // Show typing indicator after a short delay (just for UX)
      const typingTimer = setTimeout(() => {
        setIsTyping(true)
      }, 500) // 0.5-second delay

      // Show the first message after the typing indicator
      const messageTimer = setTimeout(() => {
        setMessages([
          {
            text: `Greetings ${profileData?.firstName} ${profileData?.lastName}! `,
            sender: 'bot',
            id: Date.now(),
          },
          {
            text: 'I am a simple agent who can help direct you to a specific department`s chatbot based on your question.',
            sender: 'bot',
            id: Date.now(),
          },
          {
            text: 'You can also type in the name of the department you want to chat with.',
            sender: 'bot',
            id: Date.now(),
          },
        ])
        setIsTyping(false)
      }, 2000) // 2-second delay for typing

      return () => {
        clearTimeout(typingTimer)
        clearTimeout(messageTimer)
      }
    }

    // Reset state when chat is closed
    setMessages([])
    setIsTyping(false)
    return () => {}
  }, [isOpen])

  return (
    <Box className={styles.floatingContainer}>
      {isOpen && (
        <Paper className={styles.chatWindow} elevation={5}>
          {showIframe ? (
            <Box className={styles.iframeContainer}>
              <Box className={styles.iframeHeader}>
                <Button onClick={() => setShowIframe(false)}>Back</Button>
                <Typography variant="h6">{iframeTitle}</Typography>
              </Box>
              <iframe
                src={iframeLink}
                className={styles.iframe}
                title={iframeTitle}
              />
            </Box>
          ) : (
            <>
              <Box className={styles.chatHeader}>
                <Typography variant="h6">Support Chat</Typography>
                <Button
                  className={styles.closeChatButton}
                  onClick={handleToggleChat}
                >
                  Close
                </Button>
              </Box>
              <Box className={styles.chatBody} ref={chatBodyRef}>
                {messages.map((message) => (
                  <Box
                    key={message.id}
                    className={
                      message.sender === 'user'
                        ? styles.userMessageContainer
                        : styles.botMessageContainer
                    }
                  >
                    {message.sender === 'bot' && (
                      <Avatar className={styles.botAvatar}>
                        <img
                          src={`${process.env.REACT_APP_IMAGES_URL}/assets/images/rutgers_simple_r_logo.png`}
                          alt="Rutgers Logo"
                          style={{ height: '28px' }}
                        />
                      </Avatar>
                    )}
                    {message.sender === 'user' && (
                      <Avatar sizes="xs" className={styles.userAvatar}>
                        {profileData?.firstName?.charAt(0) ?? 'N/'}
                        {profileData?.lastName?.charAt(0) ?? 'A'}
                      </Avatar>
                    )}
                    {message.type === 'button' ? (
                      <Button
                        onClick={() =>
                          handleDepartmentButtonClick(message.link)
                        }
                        className={styles.departmentButton}
                      >
                        {message.text}
                      </Button>
                    ) : (
                      <Box
                        className={
                          message.sender === 'user'
                            ? styles.userMessage
                            : styles.botMessage
                        }
                      >
                        <Typography>{message.text}</Typography>
                      </Box>
                    )}
                  </Box>
                ))}
                {isTyping && (
                  <Box className={styles.typingIndicator}>
                    <CircularProgress size={20} />
                  </Box>
                )}
              </Box>
              <Box className={styles.chatInput}>
                <TextField
                  fullWidth
                  variant="outlined"
                  placeholder="Type a message..."
                  value={inputMessage}
                  onChange={handleInputChange}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleSendMessage()
                    }
                  }}
                />
                <IconButton color="primary" onClick={handleSendMessage}>
                  <SendIcon />
                </IconButton>
              </Box>
            </>
          )}
        </Paper>
      )}

      {!isOpen && enableDialogBoolean && (
        <>
          {isMobile ? (
            <Button
              onClick={handleToggleChat}
              className={styles.mobileFloatingButton}
            >
              <HelpOutlineIcon className={styles.mobileFloatingButtonIcon} />
            </Button>
          ) : (
            <Button
              onClick={handleToggleChat}
              className={styles.floatingButton}
            >
              <HelpOutlineIcon className={styles.floatingButtonIcon} />
            </Button>
          )}
        </>
      )}
    </Box>
  )
}

export default GlobalChatCore
